// Calculates the design tokens that are no longer edited directly. Given the
// brand colours (accent, bg, header-bg, footer-bg) and the accessibility
// target, every removed token (text colours, hover shade, alt background,
// border) plus a readable text colour per palette entry is derived in OKLCH
// space, guaranteed to meet the target's contrast ratio where possible.
// The results are persisted into design_tokens so the published template
// keeps consuming the same custom properties it always has.

import { contrastRatio } from "@pgs-core/ui/lib/color";
import {
  formatOklch,
  oklchComponents,
  withOklchLightness,
  type OklchColor,
} from "@pgs-core/ui/lib/oklch";
import { readableTextOn } from "@pgs-core/ui/lib/readable-text";
import { paletteTextKey } from "@pgs-core/portal-lib/palette";
import {
  A11Y_MIN_RATIO,
  FLUID_VIEWPORT_MAX_REM,
  FLUID_VIEWPORT_MIN_REM,
  TYPOGRAPHY_SCALE_MAX,
  TYPOGRAPHY_SCALE_MIN,
  type A11yTarget,
} from "./design-tokens";

export { readableTextOn };

const ratioOn = (fg: OklchColor, bg: string) => contrastRatio(formatOklch(fg), bg) ?? 0;

/**
 * A muted variant of `text`: lightness blended toward the background as far
 * as the target allows (most-muted blend that still meets minRatio).
 */
function mutedTextOn(text: string, background: string, minRatio: number): string {
  const fg = oklchComponents(text);
  const bg = oklchComponents(background);
  if (!fg || !bg) return text;
  for (let blend = 0.45; blend >= 0; blend -= 0.05) {
    const candidate = withOklchLightness(fg, fg.l + (bg.l - fg.l) * blend);
    if (ratioOn(candidate, background) >= minRatio) return formatOklch(candidate);
  }
  return formatOklch(fg);
}

/** Lightness-shifted variant of a colour (darkened when light, and vice versa). */
function shifted(value: string, delta: number): string {
  const color = oklchComponents(value);
  if (!color) return value;
  const direction = color.l >= 0.5 ? -1 : 1;
  return formatOklch(withOklchLightness(color, color.l + direction * delta));
}

export interface DerivedTokenInput {
  accent: string;
  bg: string;
  headerBg: string;
  footerBg: string;
  palette: string[];
}

/**
 * Every calculated token, as oklch() strings. Merged over the editable tokens
 * for preview and persisted verbatim on save.
 */
export function deriveTokens(input: DerivedTokenInput, target: A11yTarget): Record<string, string> {
  const minRatio = A11Y_MIN_RATIO[target];
  const text = readableTextOn(input.bg, minRatio);

  const derived: Record<string, string> = {
    text,
    "text-muted": mutedTextOn(text, input.bg, minRatio),
    "bg-alt": shifted(input.bg, 0.035),
    border: shifted(input.bg, 0.14),
    "accent-hover": shifted(input.accent, 0.07),
    "header-text": readableTextOn(input.headerBg, minRatio),
    "footer-text": readableTextOn(input.footerBg, minRatio),
    // The hero region is no longer edited: it showcases the brand colour.
    "hero-bg": input.accent,
    "hero-text": readableTextOn(input.accent, minRatio),
  };

  input.palette.forEach((color, index) => {
    if (color.trim()) derived[paletteTextKey(index)] = readableTextOn(color, minRatio);
  });

  return derived;
}

const clampScale = (scale: number) =>
  Math.min(TYPOGRAPHY_SCALE_MAX, Math.max(TYPOGRAPHY_SCALE_MIN, scale));

const round3 = (value: number) => Number(value.toFixed(3));

/**
 * A size that grows from `minRem` at FLUID_VIEWPORT_MIN_REM to `maxRem` at
 * FLUID_VIEWPORT_MAX_REM: a plain rem value when the two sizes agree, else a
 * clamp() whose middle track is the linear interpolation between them.
 */
function fluidSize(minRem: number, maxRem: number): string {
  if (minRem === maxRem) return `${minRem}rem`;
  const slope = (maxRem - minRem) / (FLUID_VIEWPORT_MAX_REM - FLUID_VIEWPORT_MIN_REM);
  const intercept = round3(minRem - slope * FLUID_VIEWPORT_MIN_REM);
  const vw = round3(slope * 100);
  const lower = Math.min(minRem, maxRem);
  const upper = Math.max(minRem, maxRem);
  return `clamp(${lower}rem, ${intercept}rem + ${vw}vw, ${upper}rem)`;
}

/**
 * The calculated typography tokens: h6 sits one scale step above the 1rem
 * base and each level up multiplies by the ratio again (h1 = 1rem × scale⁶);
 * the display sizes continue past h1 (display-4 = scale⁷ … display-1 = scale¹⁰).
 * Each level is derived from both scales and rendered fluid — the min-scale
 * size at FLUID_VIEWPORT_MIN_REM growing to the max-scale size at
 * FLUID_VIEWPORT_MAX_REM (a plain rem when the scales agree, e.g. legacy
 * tenants without a max scale). Values are rounded to 3 decimal places so
 * floating-point math doesn't bloat the stored CSS. The base and XS body
 * sizes are pinned — only headings follow the scales.
 */
export function deriveTypographyTokens(
  scale: number,
  maxScale: number = scale
): Record<string, string> {
  const lo = clampScale(scale);
  const hi = clampScale(maxScale);
  const step = (exponent: number) => fluidSize(round3(lo ** exponent), round3(hi ** exponent));
  return {
    "display-1": step(10),
    "display-2": step(9),
    "display-3": step(8),
    "display-4": step(7),
    h1: step(6),
    h2: step(5),
    h3: step(4),
    h4: step(3),
    h5: step(2),
    h6: step(1),
    "font-size-base": "1rem",
    "font-size-xs": "0.875rem",
  };
}
